import {
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogClose,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { BehaviorSubject, distinctUntilChanged, finalize, forkJoin, map, Observable, Subscription, take } from 'rxjs';
import {
  ButtonModel,
  LookupModel,
  PbxAccountButtonTemplateModel,
  PbxAccountDeviceButtonsModel,
  PbxAccountModel,
} from '../../../../../../api/models';
import { NotificationService } from '../../../../../../notification/notification.service';
import { LocalPbxService } from '../../../../../local-pbx.service';
import { BUTTON_TYPES } from '../../constants/device-button-types';
import { ButtonTypes } from '../../enums/button-types.enum';
import { MatConfirmDialogComponent } from '../../../../../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { DateTime } from 'luxon';
import {
  DeviceButtonTemplatesListDialogComponent,
  DeviceButtonTemplatesListDialogData,
} from '../../device-button-templates/device-button-templates-list-dialog/device-button-templates-list-dialog.component';
import { FlexModule } from '@angular/flex-layout';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { DeviceButtonComponent } from '../../device-button/device-button.component';
import { DeviceButtonSharedService } from '../../services/device-button-shared.service';
import {
  DeviceButtonTemplateEditDialogComponent,
  DeviceButtonTemplateEditDialogData,
} from '../../device-button-templates/device-button-template-edit-dialog/device-button-template-edit-dialog.component';
import { MatMenuModule } from '@angular/material/menu';
import { YealinkExp43Component } from '../extensions/yealink-exp43/yealink-exp43.component';
import { ButtonTemplateType } from '../../enums/button-template-type.enum';

export interface DeviceButtonsSettingsDialogData {
  deviceId: string;
  extension: PbxAccountModel;
  isDefault?: boolean;
}

@Component({
  selector: 'app-device-base',
  templateUrl: './device-base.component.html',
  styleUrls: ['./device-base.component.scss'],
  standalone: true,
  imports: [
    FlexModule,
    MatProgressSpinner,
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDialogContent,
    MatAccordion,
    FormsModule,
    ReactiveFormsModule,
    MatDialogActions,
    MatButton,
    Angular2PromiseButtonModule,
    AsyncPipe,
    DeviceButtonComponent,
    MatMenuModule,
    YealinkExp43Component,
  ],
})
export class DeviceBaseComponent implements OnInit, OnDestroy {
  @ContentChildren('lineKeyButton', { descendants: true }) lineKeyButtons!: QueryList<ElementRef>;
  @ViewChildren(DeviceButtonComponent) deviceButtonComponents!: QueryList<DeviceButtonComponent>;
  @ViewChild(YealinkExp43Component) expansionModule!: YealinkExp43Component;

  @Input() buttonsLimit: number;

  public lookupButtonTypes: LookupModel[] = [];
  public activePage = 0;
  public activeButton = 0;
  public isExpansionMode = false;

  public readonly speedDialNumberRegex = new RegExp('^[0-9]{10}$');
  public readonly buttonTypes = ButtonTypes;

  public readonly formSubmitted$ = new BehaviorSubject<boolean>(false);

  private _device: PbxAccountDeviceButtonsModel;

  private readonly _subscriptions = new Subscription();
  private readonly _form = this._fb.group({ deviceButtons: this._fb.array([]), expansionButtons: this._fb.array([]) });

  private readonly _dataLoadedSub$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly _data: DeviceButtonsSettingsDialogData,
    private readonly _dialog: MatDialog,
    private readonly _pbxService: LocalPbxService,
    private readonly _dialogRef: MatDialogRef<DeviceBaseComponent>,
    private readonly _fb: UntypedFormBuilder,
    private readonly _notificationService: NotificationService,
    private readonly _deviceButtonSharedService: DeviceButtonSharedService,
  ) {}

  get dateInformation(): { date: string; dateMMDD: string; time: string; timePMAM: string } {
    const localTime = DateTime.local();

    return {
      date: localTime.toFormat('ccc, LLL d'),
      dateMMDD: localTime.toFormat('MM/dd'),
      time: localTime.toFormat('HH:mm'),
      timePMAM: localTime.toFormat('hh:mm a'),
    };
  }

  get extension(): PbxAccountModel {
    return this._data.extension;
  }

  get isYealinkDevice(): boolean {
    return this._device?.vendor === 'Yealink';
  }

  get isPagingAvailable(): boolean {
    return (!this.isDefaultDevice || this.isExpansionMode) && this.isYealinkDevice;
  }

  get isDefaultDevice(): boolean {
    return !!this._data.isDefault;
  }

  get deviceModelTitle(): string {
    return `${this._device?.vendor} ${this._device?.model}`;
  }

  get activeButtonsFormArray(): UntypedFormArray {
    return this.isExpansionMode ? this.expansionButtonsFormArray : this.deviceButtonsFormArray;
  }

  get allButtonsFormArray(): UntypedFormArray {
    return new UntypedFormArray([...this.deviceButtonsFormArray.controls, ...this.expansionButtonsFormArray.controls]);
  }

  get deviceButtonsFormArray(): UntypedFormArray {
    return this._form.get('deviceButtons') as UntypedFormArray;
  }

  get expansionButtonsFormArray(): UntypedFormArray {
    return this._form.get('expansionButtons') as UntypedFormArray;
  }
  get isExpansionAvailable(): boolean {
    return !!this.expansionButtonsFormArray.length;
  }

  get isPagingActive(): boolean {
    if (!this.isPagingAvailable) {
      return false;
    }

    const btns = this.deviceButtonsFormArray.getRawValue()?.slice(this.deviceButtonsFormArray.length / 3 + 1);

    return btns?.some(btn => btn.type !== 'none');
  }

  get paginatedButtons(): UntypedFormGroup[] {
    // Do not apply pagination if paging not available for device
    if (!this.isPagingAvailable && !this.isExpansionMode) {
      return this.deviceButtonsFormArray.controls as UntypedFormGroup[];
    }

    // Expansion module currently available only for yealink devices
    if (this.isExpansionMode && !this.isYealinkDevice) {
      return this.expansionButtonsFormArray.controls as UntypedFormGroup[];
    }

    let buttons;

    if (this.isExpansionMode) {
      buttons = this.expansionButtonsFormArray.controls;
    } else {
      buttons = this.deviceButtonsFormArray.controls;
    }

    let buttonsInPage = Math.floor(buttons.length / 3); // Divide into 3 pages

    // Calculate start and end indexes
    let startIndex: number = this.activePage * buttonsInPage;
    let endIndex: number = (this.activePage + 1) * buttonsInPage;

    if (!this.isExpansionMode) {
      // If no pagination and on the first page, add one more button to the first page
      if (this.activePage === 0 && !this.isPagingActive) {
        endIndex += 1;
      }

      // Adjust subsequent pages (after the first) to account for the extra button
      if (this.activePage > 0 && !this.isPagingActive) {
        startIndex += 1; // Shift start index to skip the extra button on page 1
        endIndex += 1; // Maintain same size for other pages
      }
    }

    return (buttons?.slice(startIndex, endIndex) || []) as UntypedFormGroup[];
  }

  get isFormValid(): boolean {
    return this._form?.valid;
  }

  get isDataLoaded$(): Observable<boolean> {
    return this._dataLoadedSub$.asObservable();
  }

  public ngOnInit(): void {
    this._subscriptions.add(this.subscribeDeviceData());
    this._subscriptions.add(this.subscribeFormChanges());
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public extensionNameForDevice(truncate?: number): string {
    const { firstName = '', displayName = '' } = this._data.extension || {};
    const fullName = `${firstName} ${displayName}`;

    return truncate && fullName.length > truncate ? `${fullName.substring(0, truncate)}...` : fullName;
  }

  public getLabel(btnIndex: number, labelLength: number = 7): string {
    const button = this.paginatedButtons?.at(btnIndex)?.getRawValue();

    if (!button) {
      return '';
    }

    const { label, parameter, type } = button;
    const text = label || this._deviceButtonSharedService.getDefaultLabel(type, parameter);

    return text.length > labelLength ? text.slice(0, labelLength) : text;
  }

  public onActiveButtonToggle(event: Event, btnIndex: number): void {
    const clickedPath = event?.target as Element;
    const expansion = this.deviceButtonComponents.toArray()?.[btnIndex]?.expansionPanel;
    const isActive = clickedPath?.classList?.contains('active');

    if (isActive) {
      expansion()?.close();
      this.activeButton = null;
    } else {
      expansion()?.open();
    }
  }

  public onExpansionToggle(btnIndex: number, isOpen: boolean): void {
    const buttons = this.isExpansionMode ? this.expansionModule?.getLineKeyButtons() : this.lineKeyButtons?.toArray();
    const button = buttons?.[btnIndex]?.nativeElement as Element;

    if (isOpen) {
      this.activeButton = btnIndex;
    }

    button?.classList?.toggle('active', isOpen);
  }

  public onButtonsPageChange(index?: number): void {
    this.activePage = (index ?? this.activePage + 1) % 3;

    this.onExpansionToggle(this.activeButton, false);
  }

  public onSave(): void {
    if (!this.isFormValid) {
      return;
    }

    this.formSubmitted$.next(true);

    const buttons = (this.allButtonsFormArray?.getRawValue() || []) as ButtonModel[];

    this._pbxService
      .savePbxAccountDeviceButtons(this._data.deviceId, buttons)
      .pipe(
        take(1),
        finalize(() => {
          this.formSubmitted$.next(false);
        }),
      )
      .subscribe({ next: () => this._notificationService.notifySuccess('Successfully saved device buttons') });
  }

  public onButtonsClear(): void {
    const dialogRef = this._dialog.open(MatConfirmDialogComponent, {
      data: {
        title: 'Clear Buttons',
        text: `Are you sure you’d like to clear all buttons? This will erase all of your ${this.isExpansionMode ? 'expansion module' : 'phone'} button settings.`,
        actionTitle: 'Yes',
        showCancelBtn: true,
        hideCancel: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.clearButtons();
        }
      });
  }

  public onManageTemplates(): void {
    const dialogRef = this._dialog.open<
      DeviceButtonTemplatesListDialogComponent,
      DeviceButtonTemplatesListDialogData,
      PbxAccountButtonTemplateModel
    >(DeviceButtonTemplatesListDialogComponent, {
      data: {
        isExpansionAvailable: this.isExpansionAvailable,
      },
      width: '600px',
      height: '80%',
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(template => {
        if (template) {
          this.applyButtonTemplate(template);
          this._notificationService.notifySuccess('Successfully applied button template');
        }
      });
  }

  public onSaveAsTemplate(): void {
    this._dialog.open<DeviceButtonTemplateEditDialogComponent, DeviceButtonTemplateEditDialogData, boolean>(
      DeviceButtonTemplateEditDialogComponent,
      {
        data: {
          templateId: 'new',
          deviceButtons: this.activeButtonsFormArray.getRawValue(),
          templateType: this.isExpansionMode ? ButtonTemplateType.Expansion : ButtonTemplateType.Phone,
        },
        width: '800px',
        height: '70%',
      },
    );
  }

  onDeviceSwitch(): void {
    this.isExpansionMode = !this.isExpansionMode;
    this.activePage = 0;
    this.onButtonsPageChange(0);
  }

  private applyButtonTemplate(template: PbxAccountButtonTemplateModel): void {
    const { buttons, type } = template;

    const buttonsToUpdate =
      type === ButtonTemplateType.Expansion ? this.expansionButtonsFormArray : this.deviceButtonsFormArray;

    for (let i = 0; i < buttonsToUpdate.length; i++) {
      const button = buttons[i];
      if (!button) {
        break;
      }

      buttonsToUpdate.at(i).patchValue(button);
    }
  }

  private populateButtonsForm(buttons: ButtonModel[]): void {
    const deviceButtonsArray = this.deviceButtonsFormArray;
    const expansionButtonsArray = this.expansionButtonsFormArray;

    buttons.forEach(button => {
      const buttonGroup = this._deviceButtonSharedService.createButtonGroup(button);

      if (['P', 'Line Key'].includes(button.name)) {
        deviceButtonsArray.push(buttonGroup);
      } else if (button.name?.startsWith('Ext 1')) {
        expansionButtonsArray.push(buttonGroup);
      }
    });
  }

  private mapLookupButtonTypes(): void {
    this.lookupButtonTypes = (BUTTON_TYPES || [])
      .filter(type => !this._device?.modes || this._device.modes.includes(type.value))
      .map(btn => ({ id: btn.value, text: btn.title }));
  }

  private clearButtons(): void {
    this.activeButtonsFormArray.controls.forEach(group => {
      const type = group?.get('type');

      type.setValue(ButtonTypes.None);
      this._deviceButtonSharedService.onButtonTypeChange(ButtonTypes.None, group as UntypedFormGroup);
    });

    this._form.updateValueAndValidity();
  }

  private subscribeDeviceData(): Subscription {
    const { deviceId } = this?._data;

    return forkJoin([
      this._pbxService.getPbxAccountDeviceButtons(deviceId, this.buttonsLimit + 60).pipe(take(1)),
      this._pbxService.getAccountsLookupForDevice().pipe(take(1)),
    ])
      .pipe(finalize(() => this._dataLoadedSub$.next(true)))
      .subscribe({
        next: ([device, parameterAccounts]) => {
          if (!device) {
            this._dialogRef.close();
            this._notificationService.notifyDanger('Device not found');
            return;
          }

          this._device = device;

          if (!device.buttons?.length) {
            this._dialogRef.close();
            this._notificationService.notifyDanger(`Buttons for ${this.deviceModelTitle} not available`);
            return;
          }

          this.mapLookupButtonTypes();
          this._deviceButtonSharedService.prepareData(parameterAccounts?.data || [], this.extension);
          this.populateButtonsForm(this._device.buttons);
        },
        error: () => {
          this._dialogRef.close();
        },
      });
  }

  private subscribeFormChanges(): Subscription {
    return this.allButtonsFormArray.valueChanges
      .pipe(
        map(() => this.isPagingActive),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        const openPanel = this.deviceButtonComponents.toArray().find(btn => btn.expansionPanel()?.expanded);

        openPanel?.expansionPanel().close();
      });
  }
}
